import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useInactivityTimeout } from '../../hooks/useInactivityTimeout';
import { usePublicationControls } from './usePublicationControls';

export type MfaState = 'loading' | 'enroll' | 'challenge' | 'verified' | 'error';

type MfaResult = { identity: string | null; status: MfaState };

export function useAdminSession(
  isAuthorized: boolean,
  userEmail: string | null,
  setNotification: (n: { message: string; type: 'error' | 'success' | 'warning' } | null) => void
) {
  const { logout } = useAuth();
  const [clientIP, setClientIP] = useState<string>('unknown');
  // Results are bound to the email they were checked for, so a change of
  // authorized identity makes the effective state 'loading' in the very same
  // render — before any effect can run privileged work for the old result.
  const [mfaResult, setMfaResult] = useState<MfaResult>({ identity: null, status: 'loading' });
  const [mfaRetryNonce, setMfaRetryNonce] = useState(0);
  const latestRef = useRef({ isAuthorized, userEmail });
  latestRef.current = { isAuthorized, userEmail };

  const mfaState: MfaState =
    isAuthorized && userEmail && mfaResult.identity === userEmail
      ? mfaResult.status
      : 'loading';

  // Bound to the render's email: a callback handed to a previous identity's
  // flow keeps that email captured, so a stale challenge/enrollment callback
  // can never act on a new user's state — even when the new user is mid-flow.
  const setMfaState = useCallback(
    (status: MfaState) => {
      const email = userEmail;
      const { isAuthorized: authorized, userEmail: current } = latestRef.current;
      if (!authorized || !email || current !== email) return;
      setMfaResult((prev) => {
        const currentStatus = prev.identity === email ? prev.status : 'loading';
        const allowed =
          (currentStatus === 'challenge' && status === 'verified') ||
          (currentStatus === 'enroll' && status === 'verified');
        return allowed ? { identity: email, status } : prev;
      });
    },
    [userEmail]
  );

  // Session timeout — auto-logout after 30 min of inactivity
  const handleInactivityTimeout = useCallback(async () => {
    await logout();
    window.location.reload();
  }, [logout]);
  useInactivityTimeout(handleInactivityTimeout, isAuthorized && mfaState === 'verified');

  const getClientIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json', { timeout: 5000 } as any);
      if (response.ok) {
        const data = await response.json();
        return data.ip || 'unknown';
      }
    } catch (error) {
      console.log('Could not fetch IP address:', error);
    }
    return 'unknown';
  };

  const logAdminAction = useCallback(async (action: string, details?: any) => {
    try {
      await supabase.rpc('log_admin_action', {
        admin_email_param: userEmail || 'unknown',
        action_param: action,
        ip_param: clientIP,
        details_param: details ? JSON.stringify(details) : null
      });
    } catch (error) {
      console.error('Failed to log admin action:', error);
    }
  }, [userEmail, clientIP]);

  const retryMfaCheck = useCallback(() => {
    setMfaResult({ identity: null, status: 'loading' });
    setMfaRetryNonce((n) => n + 1);
  }, []);

  // Check MFA status whenever the authorized identity changes or a retry is
  // requested. Any timeout, error, rejection or null response is terminal for
  // that check — a late success can never unlock the workspace. Results are
  // tagged with the checked email so they can only ever apply to it.
  useEffect(() => {
    if (!isAuthorized || !userEmail) {
      setMfaResult({ identity: null, status: 'loading' });
      return;
    }
    const checkedEmail = userEmail;
    let settled = false;
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) { settled = true; setMfaResult({ identity: checkedEmail, status: 'error' }); }
    }, 5000);

    const checkMFA = async () => {
      try {
        const { data, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled || settled) return;
        settled = true;
        clearTimeout(timeout);
        if (aalError || !data) { setMfaResult({ identity: checkedEmail, status: 'error' }); return; }
        if (data.currentLevel === 'aal2') {
          setMfaResult({ identity: checkedEmail, status: 'verified' });
        } else if (data.currentLevel === 'aal1' && data.nextLevel === 'aal2') {
          setMfaResult({ identity: checkedEmail, status: 'challenge' });
        } else if (data.currentLevel === 'aal1' && data.nextLevel === 'aal1') {
          setMfaResult({ identity: checkedEmail, status: 'enroll' });
        } else {
          setMfaResult({ identity: checkedEmail, status: 'error' });
        }
      } catch {
        if (!cancelled && !settled) { settled = true; clearTimeout(timeout); setMfaResult({ identity: checkedEmail, status: 'error' }); }
      }
    };
    checkMFA();
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [isAuthorized, userEmail, mfaRetryNonce]);

  useEffect(() => {
    getClientIP().then(ip => setClientIP(ip));
  }, []);

  useEffect(() => {
    const logAccess = async () => {
      if (isAuthorized && userEmail && mfaState === 'verified') {
        await logAdminAction('admin_console_access', {
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent
        });
      }
    };
    logAccess();
  }, [isAuthorized, userEmail, mfaState, logAdminAction]);

  const publication = usePublicationControls(
    isAuthorized && mfaState === 'verified',
    logAdminAction,
    setNotification
  );

  return {
    logout,
    clientIP,
    mfaState,
    setMfaState,

    logAdminAction,
    retryMfaCheck,
    ...publication,
  };
}
